import { useApolloClient } from "@apollo/client";
import { assertNever, getDataOrNull, omit } from "@msys/common";
import { useTranslate } from "@tolgee/react";
import React from "react";
import {
  FilterChip,
  FilterChipGroup,
} from "../../commons/filters/FilterChip.js";
import {
  TasksListQueryVariables,
  useAssigneeFilterChipsQuery,
  useClientFilterChipsQuery,
  useContactFilterChipsQuery,
  useProjectFilterChipsQuery,
} from "../../main-routes/tasks/TasksList.generated.js";

type Filters = NonNullable<TasksListQueryVariables["filter"]>;

export function useTasksActiveFilterChips<F extends Filters>({
  filters,
  setFilters,
}: {
  filters: F;
  setFilters?: React.Dispatch<React.SetStateAction<F>>;
}): Array<JSX.Element> {
  const { t } = useTranslate(["Global", "Tasks"]);

  const chips: Array<JSX.Element> = [];

  if (filters.status && filters.status.length) {
    chips.push(
      <FilterChipGroup
        key="status"
        label={t("Status", { ns: "Global" })}
        getValueLabel={value =>
          value === "DONE"
            ? t("Completed", { ns: "Global" })
            : value === "OPEN"
              ? t("Open", { ns: "Global" })
              : assertNever(value)
        }
        values={filters.status}
        setValues={
          setFilters
            ? newValue =>
                newValue && newValue.length > 0
                  ? setFilters(filters => ({
                      ...filters,
                      status: newValue,
                    }))
                  : setFilters(filters => omit(filters, "status") as F)
            : undefined
        }
      />
    );
  }

  if (filters.assigneeIds?.length) {
    chips.push(
      <AssigneeFilterChipWrapper
        key="assignee"
        filterAssigneeIds={filters.assigneeIds}
        onDelete={
          setFilters
            ? () =>
                setFilters(currentFilters => {
                  const { assigneeIds, ...rest } = currentFilters;
                  return rest as F;
                })
            : undefined
        }
      />
    );
  }

  if (filters.linkedCrmUserId) {
    chips.push(
      <ContactFilterChipWrapper
        key="contact"
        filterLinkedCrmUserId={filters.linkedCrmUserId}
        onDelete={
          setFilters
            ? () =>
                setFilters(currentFilters => {
                  const { linkedCrmUserId, ...rest } = currentFilters;
                  return rest as F;
                })
            : undefined
        }
      />
    );
  }

  if (filters.linkedCrmOrganisationId) {
    chips.push(
      <ClientFilterChipWrapper
        key="client"
        filterLinkedCrmOrganisationId={filters.linkedCrmOrganisationId}
        onDelete={
          setFilters
            ? () =>
                setFilters(currentFilters => {
                  const { linkedCrmOrganisationId, ...rest } = currentFilters;
                  return rest as F;
                })
            : undefined
        }
      />
    );
  }

  if (filters.linkedProjectId) {
    chips.push(
      <ProjectFilterChipWrapper
        key="project"
        filterLinkedProjectId={filters.linkedProjectId}
        onDelete={
          setFilters
            ? () =>
                setFilters(currentFilters => {
                  const { linkedProjectId, ...rest } = currentFilters;
                  return rest as F;
                })
            : undefined
        }
      />
    );
  }

  return chips;
}

function AssigneeFilterChipWrapper({
  filterAssigneeIds,
  onDelete,
}: {
  filterAssigneeIds: string[];
  onDelete?: () => void;
}) {
  const { t } = useTranslate(["Tasks"]);
  const client = useApolloClient();
  const query = useAssigneeFilterChipsQuery({
    client,
    variables: { filterAssigneeIds },
  });
  const assignee = query.data?.organisationMemberships.edges.map(
    e => e.node
  )[0];

  return (
    <FilterChip
      label={t("Assignee", { ns: "Tasks" })}
      value={assignee?.fullname}
      onDelete={onDelete}
    />
  );
}

function ContactFilterChipWrapper({
  filterLinkedCrmUserId,
  onDelete,
}: {
  filterLinkedCrmUserId: string;
  onDelete?: () => void;
}) {
  const { t } = useTranslate(["Tasks"]);
  const client = useApolloClient();
  const query = useContactFilterChipsQuery({
    client,
    variables: { filterLinkedCrmUserIds: [filterLinkedCrmUserId] },
  });
  const person = getDataOrNull(query.data?.crmPersons)?.edges.map(
    e => e.node
  )[0];

  return (
    <FilterChip
      label={t("Contact", { ns: "Tasks" })}
      value={person?.fullname}
      onDelete={onDelete}
    />
  );
}

function ClientFilterChipWrapper({
  filterLinkedCrmOrganisationId,
  onDelete,
}: {
  filterLinkedCrmOrganisationId: string;
  onDelete?: () => void;
}) {
  const { t } = useTranslate(["Tasks"]);
  const client = useApolloClient();
  const query = useClientFilterChipsQuery({
    client,
    variables: {
      filterLinkedCrmOrganisationIds: [filterLinkedCrmOrganisationId],
    },
  });
  const company = getDataOrNull(query.data?.crmCompanies)?.edges.map(
    e => e.node
  )[0];

  return (
    <FilterChip
      label={t("Client", { ns: "Tasks" })}
      value={company?.title}
      onDelete={onDelete}
    />
  );
}

function ProjectFilterChipWrapper({
  filterLinkedProjectId,
  onDelete,
}: {
  filterLinkedProjectId: string;
  onDelete?: () => void;
}) {
  const { t } = useTranslate(["Tasks"]);
  const client = useApolloClient();
  const query = useProjectFilterChipsQuery({
    client,
    variables: { filterLinkedProjectIds: [filterLinkedProjectId] },
  });
  const project = getDataOrNull(query.data?.projects)?.edges.map(
    e => e.node
  )[0];

  return (
    <FilterChip
      label={t("Project", { ns: "Tasks" })}
      value={project?.title}
      onDelete={onDelete}
    />
  );
}
